import { Box, IconButton, TextField, Typography } from '@mui/material';
import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import JobPreferencesAutoComplete from './CustomFormFields/JobPreferencesAutoComplete';
import { getLocationStatesCitiesZip, getSearchFilterOptions } from 'app/services/SharedServices/SharedServices';
import { getMultiSelectOptions } from 'app/components/GlobalSearch/helper';
import { getSortedData, getUnique } from 'app/helpers/arrayHelpers';
import { FilterType } from 'app/enums/Common';
import { JobMatchFormFields, educationOptions, tabs } from './helper';
import { DateRangePicker } from 'app/ComponentLibrary/DateRangeNew';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import moment from 'moment';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { ChipsContainer } from 'app/ComponentLibrary/Filter/ChipsContainer';
import { useSelector } from 'react-redux';
import { selectJobPreferencesData, selectJobPreferencesModalProps } from './store/JobPreferences.selectors';
import { getAllCitiesAndZips } from '@AMIEWEB/GlobalSearch/Candidate/FilterUtils';
import { IStateCityZip } from 'app/models/Candidate/IStateCityZip';
import { cloneDeep, debounce } from 'lodash';
import { Authorized } from 'oidc/userHelper';
import { userRoles } from 'oidc/userRoles';
import { selectUser } from 'oidc/user.selectors';
import { Tooltip } from '@mui/material';
import { CustomTooltip } from '@AMIEWEB/Common';
import { candidateDetailsSelection } from '../../Profile/CandidateDetails.selector';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import HearingIcon from '@mui/icons-material/Hearing';
import { Add } from '@mui/icons-material';
import { isEqual } from 'lodash';

const useStyles = makeStyles<{ expanded: boolean }>()((theme, expanded) => ({
  preferencename: {
    width: '780px',
    fontWeight: 500,
    '& .MuiFilledInput-input': {
      width: '636px',
    },
    '& .MuiFormLabel-root.Mui-focused': {
      color: '#9F9F9F',
    },
  },
  location: {
    width: '168px',
    marginRight: '12px',
    '& .MuiFormLabel-root.Mui-focused': {
      color: '#9F9F9F',
    },
  },
  slider: {
    width: '505px',
    marginLeft: '12px',
  },
  sliderValues: {
    width: '75px',
    margin: '13px',
    '& .MuiOutlinedInput-adornedStart': {
      padding: 0,
    },
    '& .MuiOutlinedInput-input': {
      padding: '18.5px 0px',
    },
    '& .MuiInputBase-root': {
      padding: 0,
    },
    '& .MuiOutlinedInput-root': {
      padding: 0,
    },
  },
  sliderValue: {
    width: '70px',
    margin: '13px',
  },
  notes: {
    backgroundColor: '#FFFFFF',
    marginTop: '1%',
    marginBottom: '8px',
    '& .MuiInputBase-root': {
      width: '740px',
      height: '200px',
      display: 'inline-flex',
      flexDirection: 'column',
      margin: '2px',
    },
    '& .MuiOutlinedInput-root': {
      margin: '2px',
      position: 'initial',
    },
    '& label': {
      '&.Mui-focused': {
        color: '#C0C0C0',
      },
    },
  },
  date: {
    width: '318px',
    height: '51px',
    '& .MuiInput-formControl': {
      margin: 0,
    },
    '& .MuiFormControl-root': {
      width: '318px',
    },
    '& .MuiInputBase-input': {
      height: '31px',
      padding: '16px 12px 7px',
    },
    '& label': {
      '&.Mui-focused': {
        color: '#C0C0C0',
      },
    },
  },
  container: {
    height: expanded ? '100%' : '453px',
    display: 'flex',
  },
  validationLoader: {
    position: 'absolute',
    left: 'calc(50% - 20px)',
    top: '50%',
  },
  dialogueContent: {
    padding: 0,
    '& .MuiDialogContent-root': {
      padding: 0,
    },
    '& .dialogContentRoot': {
      padding: 0,
    },
  },
  fullScreenContainer: {
    height: '100%',
    overflow: 'hidden',
    overflowY: 'scroll',
  },

  label: {
    margin: '24px 0 12px',
  },
  labelText: {
    fontSize: 'normal normal medium 14px/26px Roboto',
    fontWeight: 500,
  },
  skillset: {
    width: 368,
    padding: '10px 10px 0 0px',
  },
  flex: {
    display: 'flex',
  },
  locations: {
    width: 183,
    padding: '5px 5px 0px 0px',
  },
  span: {
    fontWeight: 'normal',
    color: '#00000041',
    margin: '0 5px',
  },
  education: {
    width: 318,
  },
  checkBox: {
    '& .MuiButtonBase-root': {
      padding: 0,
    },
    '& .MuiCheckbox-root': {
      padding: 0,
    },
  },
  checkBoxWrapper: {
    magin: 5,
  },
  dateRangeLabel: {
    color: 'rgba(0,0,0,0.7)',
  },
  labelError: {
    fontSize: 'normal normal medium 14px/26px Roboto',
    fontWeight: 500,
    color: '#D90000',
  },
  spanError: {
    fontWeight: 'normal',
    color: '#D90000',
    margin: '0 5px',
  },
}));

interface OrderPreferencesFormProps {
  expanded: boolean;
  selectedTab: string;
  setDisabled: any;
  isSaving: boolean;
}

const getDisciplinesMultiSelectOptions = options => {
  const array = options?.reduce(
    (resultArray, item) => [
      ...resultArray,
      {
        value: item?.value?.slice(item?.value?.indexOf('_') + 1),
        name: item.name,
        corelation: item.value,
      },
    ],
    [],
  );
  const sortedData = getSortedData(array || [], FilterType.string);
  return sortedData;
};

const OrderPreferencesForm = (props: OrderPreferencesFormProps) => {
  const { classes } = useStyles({ expanded: false });
  const { control, watch, setValue, getValues } = useFormContext();
  const { t } = useTranslation();
  const { selectedTab, setDisabled, isSaving } = props;
  const { jobPreferencesData } = useSelector(selectJobPreferencesData);
  const { id } = useSelector(selectJobPreferencesModalProps);
  const user = useSelector(selectUser);
  const candidate = useSelector(candidateDetailsSelection);
  const [dateRangeDisplay, setDateRangeDisplay] = React.useState<any | undefined | null>(null);
  const [statesOptions, setStatesOptions] = React.useState<Array<any>>([]);
  const [cityOptions, setCityOptions] = React.useState<Array<any>>([]);
  const [zipsOptions, setZipsOptions] = React.useState<Array<any>>([]);
  const [disciplineOptions, setDisciplineOptions] = React.useState<Array<any>>([]);
  const [specialityOptions, setSpecialityOptions] = React.useState<Array<any>>([]);
  const [allSpecialities, setAllSpecialities] = React.useState<Array<any>>([]);
  const [shiftTypesOptions, setShiftTypesOptions] = React.useState<Array<any>>([]);
  const [locationChips, setLocationChips] = React.useState<any>({ locations: [] });
  const [skillSetChips, setSkillSetChips] = React.useState<any>({ skillSets: [] });
  const [orderDateRangeChip, setOrderDateRangeChip] = React.useState<any>({ startDate: [] });
  const [preferenceDetail, setPreferenceDetail] = React.useState<any>({});
  const NameErrorText = 'Maximum length occupied';
  const radiusErrorText = 'Radius range is from 0 to 999';
  const [preferenceNameErrorText, setPreferenceNameErrorText] = React.useState('');
  const [error, setError] = React.useState({
    name: false,
    radius: false,
    notes: false,
    skillSets: false,
    locations: false,
  });

  const prefName = watch(JobMatchFormFields.preferenceSetName);
  const locationValues = watch(JobMatchFormFields.locations);
  const skillSetValues = watch(JobMatchFormFields.skillSets);
  const notes = watch(JobMatchFormFields.notes);
  const day = watch(JobMatchFormFields.day);
  const evening = watch(JobMatchFormFields.evening);
  const night = watch(JobMatchFormFields.night);
  const discipline = watch(JobMatchFormFields.discipline);
  const speciality = watch(JobMatchFormFields.speciality);
  const state = watch(JobMatchFormFields.state);
  const city = watch(JobMatchFormFields.city);
  const zip = watch(JobMatchFormFields.zip);
  const radius = watch(JobMatchFormFields.radius);
  const orderStartDateRange = watch(JobMatchFormFields.orderStartDateRange);
  const shifts = watch(JobMatchFormFields.shifts);
  const education = watch(JobMatchFormFields.education);

  const { locations = [] } = getValues();
  const hasTruthyRadius = locations?.some(obj => !!obj.radius);

  const FieldLabel = ({
    label,
    secondaryText,
    error,
    errorText,
  }: {
    label: string;
    secondaryText?: string;
    error?: boolean;
    errorText?: string;
  }) => {
    return (
      <div className={classes.label}>
        <Typography className={error ? classes.labelError : classes.labelText}>
          {t(label)}
          {secondaryText && <span className={error ? classes.spanError : classes.span}>({secondaryText})</span>}
        </Typography>
        {error && <Typography className={error ? classes.labelError : classes.labelText}>{t(errorText)}</Typography>}
      </div>
    );
  };

  const arraysEqual = (arr1, arr2) => {
    return isEqual(arr1, arr2);
  };

  const compareDates = (objA, objB) => {
    if (!objA && !objB) {
      return true; // Both objects are null, consider them equal
    }

    if (!objA || !objB) {
      return false; // One object is null while the other is not, consider them not equal
    }

    const dateAStart = new Date(objA.startDate);
    const dateAEnd = new Date(objA.endDate);
    const dateBStart = new Date(objB.startDate);
    const dateBEnd = new Date(objB.endDate);
    dateAStart.setHours(0, 0, 0, 0);
    dateAEnd.setHours(0, 0, 0, 0);
    dateBStart.setHours(0, 0, 0, 0);
    dateBEnd.setHours(0, 0, 0, 0);

    const startDatesEqual = dateAStart.getTime() === dateBStart.getTime();
    const endDatesEqual = dateAEnd.getTime() === dateBEnd.getTime();

    return startDatesEqual && endDatesEqual;
  };

  const enableSaveOnChanges = () => {
    if (Authorized([userRoles.recruitment_TeamMember, userRoles.recruitment_Leadership], user.userInfo)) {
      const values = getValues();
      const { preferenceSetName, skillSets, locations, orderStartDateRange, education, notes } = values;
      if (isSaving) {
        setDisabled(false);
      } else {
        if (id && id != '' && id != ' ') {
          const data = jobPreferencesData.filter(item => item.id === id)?.[0];
          const shiftIds = values.shifts && values.shifts.map(item => item.value);
          if (preferenceSetName && preferenceSetName !== '' && skillSets?.length > 0 && locations?.length > 0) {
            if (preferenceSetName !== data.preferenceSetName) {
              const nameErr = handlePreferenceName(preferenceSetName);
              setDisabled(nameErr);
              return;
            } else if (locations?.length > 0 && !arraysEqual(locations, data?.locations)) {
              setDisabled(true);
              return;
            } else if (skillSets && skillSets.length > 0 && !arraysEqual(skillSets, data?.skillSets)) {
              setDisabled(true);
              return;
            } else if (!compareDates(orderStartDateRange, data?.orderStartDateRange)) {
              setDisabled(true);
              return;
            } else if (!arraysEqual(education, data?.education)) {
              setDisabled(true);
              return;
            } else if (notes !== data?.notes) {
              setDisabled(true);
              return;
            } else if (!arraysEqual(shiftIds, data.shiftIds)) {
              setDisabled(true);
              return;
            } else {
              setDisabled(false);
              return;
            }
          } else {
            setDisabled(false);
            return;
          }
        } else {
          if (preferenceSetName && preferenceSetName != '' && preferenceSetName != ' ') {
            if (skillSets?.length <= 0) {
              setDisabled(false);
              setError({ ...error, skillSets: true });
            } else if (locations?.length <= 0) {
              setDisabled(false);
              setError(prev => ({ ...prev, locations: true }));
            } else {
              handlePreferenceName(preferenceSetName);
            }
          } else {
            setDisabled(false);
          }
        }
      }
    } else {
      setDisabled(false);
      return false;
    }
  };

  const scopeDropDownValues = () => {
    const values = getValues();
    const { city, state, discipline } = values;
    // ********** Handle the scoping of locations ***********/
    if (city && state) {
      fetchCities([state?.name], [city?.name]);
    } else if (city) {
      fetchCities([], [city?.name]);
    } else if (state) {
      fetchCities([state?.name], []);
    } else if (!state && !city) {
      fetchCities(
        statesOptions.map(item => item?.name),
        [],
      );
    }
    /********* Handle the scoping of skillsets ************/
    if (discipline) {
      findSpecialities(discipline);
    }
  };

  const getLabel = (property, value) => {
    let label = '';
    if (property === 'locations') {
      if (value?.state) {
        label = label + (value?.state?.value || value?.state) + '/';
      }
      if (value?.city) {
        label = label + (value?.city?.name || value?.city) + '/';
      }
      if (value?.zip) {
        label = label + (value?.zip?.name || value?.zip) + '/';
      }
      if (value?.radius) {
        label = label + value?.radius + 'mi' + '/';
      }
    } else {
      if (value?.discipline) {
        label = label + (value?.discipline?.name || value?.discipline) + '-';
      }
      if (value?.speciality) {
        label = label + (value?.speciality?.name || value?.speciality) + '/';
      }
    }

    const text = label?.substring(0, label?.length - 1);
    return text;
  };

  const getPreferencesChips = (field, val?) => {
    if (field === 'locations') {
      const chipsData = [...locationChips?.locations];
      val?.forEach(element => {
        const label = getLabel(field, element);
        const object = {
          label: label,
          enableDelete: true,
          value: element,
        };
        chipsData.push(object);
      });
      setLocationChips({ locations: chipsData });
      if (chipsData?.length <= 0) {
        setError(prev => ({
          ...prev,
          locations: true,
        }));
      } else {
        setError(prev => ({
          ...prev,
          locations: false,
        }));
      }
    } else {
      const chipsData = [...skillSetChips?.skillSets];
      val?.forEach(element => {
        const label = getLabel(field, element);
        const object = {
          label: label,
          enableDelete: true,
          value: element,
        };
        chipsData.push(object);
      });
      setSkillSetChips({ skillSets: chipsData });
      if (chipsData?.length <= 0) {
        setError({
          ...error,
          skillSets: true,
        });
      } else {
        setError({
          ...error,
          skillSets: false,
        });
      }
    }
  };
  const handleChipCreation = field => {
    const values = getValues();
    if (field === 'locations') {
      const { state, city, zip, radius, locations } = values;
      const label = getLabel(field, values);
      const value = {
        city: city?.name,
        radius: radius && radius !== '' ? radius : 0,
        state: state?.value,
        zip: zip?.name,
        stateName: state?.name,
        cityId: parseInt(city?.id) || 0,
        zipId: parseInt(zip?.id) || 0,
      };
      const object = {
        label: label,
        enableDelete: true,
        value: value,
      };
      const chipsData = [...locationChips?.locations, object];
      setLocationChips({ locations: chipsData });
      setValue(field, [...locations, value]);
      setValue(JobMatchFormFields.city, undefined);
      setValue(JobMatchFormFields.state, undefined);
      setValue(JobMatchFormFields.zip, undefined);
      setValue(JobMatchFormFields.radius, '');
      setError(prev => ({
        ...prev,
        locations: false,
      }));
    } else {
      const { discipline, speciality, skillSetsArray, skillSets } = values;
      const label = getLabel(field, values);
      const value = {
        discipline: discipline?.name,
        specialty: speciality?.name,
      };
      const object = {
        label: label,
        enableDelete: true,
        value: value,
      };
      const data = {
        disciplineId: discipline?.value,
        specialtyId: speciality?.value,
      };
      const fieldData = [...skillSets, data];
      const chipsData = [...skillSetChips?.skillSets, object];
      setSkillSetChips({ skillSets: chipsData });
      setValue(field, [...skillSetsArray, value]);
      setValue(JobMatchFormFields.skillSets, fieldData);
      setValue(JobMatchFormFields.discipline, undefined);
      setValue(JobMatchFormFields.speciality, undefined);
      setError({
        ...error,
        skillSets: false,
      });
    }
  };

  const handleChipDeletion = (group, chips, index) => {
    const values = getValues();
    if (group === 'locations') {
      const chips = cloneDeep(locationChips?.locations);
      const { locations } = values;
      const data = cloneDeep(locations);
      data?.splice(index, 1);
      chips?.splice(index, 1);
      setLocationChips({ locations: chips });
      setValue('locations', data);
      if (data?.length <= 0) {
        setError({
          ...error,
          locations: true,
        });
      }
    } else {
      const { skillSetsArray, skillSets } = values;
      const chips = cloneDeep(skillSetChips?.skillSets);
      const data = cloneDeep(skillSets);
      const arr = cloneDeep(skillSetsArray);
      data?.splice(index, 1);
      chips?.splice(index, 1);
      arr?.splice(index, 1);
      setSkillSetChips({ skillSets: chips });
      setValue('skillSets', data);
      setValue('skillSetsArray', arr);
      if (data?.length <= 0) {
        setError({
          ...error,
          skillSets: true,
        });
      }
    }
  };

  const handlePreferenceName = text => {
    let nameError = false;
    let defaultName = '';
    let disableSave = false;
    if (id) {
      const data = jobPreferencesData?.filter(item => item?.id === id)?.[0];
      defaultName = data?.preferenceSetName;
    }
    if (text.length > 0) {
      jobPreferencesData?.map(item => {
        if (item?.preferenceSetName !== defaultName) {
          if (item?.preferenceSetName?.toLowerCase() === text?.toLowerCase()) {
            nameError = true;
          }
        }
      });
      if (nameError) {
        setError({
          ...error,
          name: true,
        });
        setPreferenceNameErrorText('Preference Set name already exists');
        setDisabled(false);
        disableSave = false;
      } else {
        setError({
          ...error,
          name: false,
        });
        setPreferenceNameErrorText('');
        setDisabled(true);
        disableSave = true;
      }

      if (text === defaultName) {
        setDisabled(false);
        disableSave = false;
      } else if (nameError && text != defaultName) {
        setDisabled(true);
        disableSave = true;
      } else {
        setDisabled(true);
        disableSave = true;
      }
      return disableSave;
    }
  };

  const handleChange = (fieldName, value) => {
    if (fieldName === JobMatchFormFields.preferenceSetName) {
      if (value?.length <= 30) {
        if (value === '' || value === undefined) {
          setError({ ...error, name: true });
          setDisabled(false);
          setPreferenceNameErrorText('Preference Set name is required');
          setValue(fieldName, value);
        } else {
          if (id) {
            handlePreferenceName(value);
          } else {
            setError({ ...error, name: false });
            setPreferenceNameErrorText('');
          }
          setValue(fieldName, value);
        }
      } else {
        setError({ ...error, name: true });
        setDisabled(false);
        setPreferenceNameErrorText(NameErrorText);
      }
    } else if (fieldName === JobMatchFormFields.notes) {
      if (value?.length < 1001) {
        setError({ ...error, notes: false });
        setValue(fieldName, value);
      } else {
        setError({ ...error, notes: false });
        setDisabled(false);
      }
    } else if (fieldName === JobMatchFormFields.radius) {
      if (value !== undefined && value !== '' && !isNaN(value)) {
        if (value == Math.floor(value)) {
          if (parseInt(value) > -1 && parseInt(value) < 1000) {
            setError({ ...error, radius: false });
            setValue(fieldName, parseInt(value));
          } else {
            setError({ ...error, radius: true });
          }
        } else {
          setValue(fieldName, parseInt(value?.substr(0, value?.indexOf('.')) || ''));
        }
      } else {
        setValue(fieldName, undefined);
      }
    } else if (fieldName === JobMatchFormFields.state) {
      setValue(fieldName, value);
      if (value) {
        fetchCities([value?.name], []);
      } else {
        setValue(JobMatchFormFields.city, '');
        setValue(JobMatchFormFields.zip, '');
        setValue(JobMatchFormFields.radius, '');
      }
    } else {
      setValue(fieldName, value);
      scopeDropDownValues();
    }
  };

  const getDropDownOptions = async () => {
    //Make all the network calls parallely to reduce execution time
    const [disciplineLookups, specialtyLookups, statesLookups, shiftLookups] = await Promise.all([
      getSearchFilterOptions('disciplines'),
      getSearchFilterOptions('discipline_specialties'),
      getSearchFilterOptions('states'),
      getSearchFilterOptions('shiftlist'),
    ]);

    // Use the retrieved data as needed
    const statesOption = getMultiSelectOptions(statesLookups || []);
    const disciplineOption = getMultiSelectOptions(disciplineLookups || []);
    const specialityOption = getMultiSelectOptions(getDisciplinesMultiSelectOptions(specialtyLookups || []));
    const shiftTypesOption = getMultiSelectOptions(shiftLookups);
    const uniqueSpecialityOptions = getUnique(
      specialityOption.filter(x => x.name),
      'name',
    );
    setShiftTypesOptions(shiftTypesOption);
    setStatesOptions(statesOption);
    setDisciplineOptions(disciplineOption);
    setSpecialityOptions(uniqueSpecialityOptions);
    setAllSpecialities(specialityOption);
    fetchCities([statesOption?.[0]?.name], []);
  };

  const onTextSearch = async (key: string, searchMode: 'c' | 'z') => {
    let filteredCities: IStateCityZip[] = [];
    let filteredZips: IStateCityZip[];
    const { state, city } = getValues();
    const selectedStates = state ? [state.name] : statesOptions.map(item => item.name);
    const selectedCities = city ? [city.name] : [];
    if (searchMode === 'c') {
      filteredCities = (await getLocationStatesCitiesZip({
        states: selectedStates,
        cities: [],
        searchMode: searchMode,
        searchValue: key,
      })) as IStateCityZip[];
      const cities = getAllCitiesAndZips(filteredCities).cities;
      setCityOptions(cities);
    } else if (searchMode === 'z') {
      filteredZips = (await getLocationStatesCitiesZip({
        states: selectedStates,
        cities: selectedCities,
        searchMode: searchMode,
        searchValue: key,
      })) as IStateCityZip[];
      const zips = getAllCitiesAndZips(filteredZips).zips;
      setZipsOptions(zips);
    }
  };

  const handleScroll = newValue => {
    const ElementId = tabs[newValue]?.id;
    if (ElementId) {
      const element = document.getElementById(ElementId);
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const clearDates = () => {
    setDateRangeDisplay(null);
    setOrderDateRangeChip({ startDate: [] });
    setValue(JobMatchFormFields.orderStartDateRange, null);
  };

  const formatForDateRangePicker = e => {
    if (e?.startDate && e?.endDate) {
      const dates = {
        startDate: moment(e.startDate).format('YYYY-MM-DD'),
        endDate: moment(e.endDate).format('YYYY-MM-DD'),
      };
      const obj = {
        label: `${moment(dates.startDate).format('MM/DD/YYYY')} - ${moment(dates.endDate).format('MM/DD/YYYY')}`,
        enableDelete: true,
        value: dates,
      };
      setOrderDateRangeChip({ startDate: [obj] });
      setValue(JobMatchFormFields.orderStartDateRange, dates);
      return dates;
    }
    setValue(JobMatchFormFields.orderStartDateRange, null);
    return null;
  };

  React.useEffect(() => {
    getDropDownOptions();
  }, []);

  React.useEffect(() => {
    handleScroll(selectedTab);
  }, [selectedTab]);

  React.useEffect(() => {
    /****** Used to update the states of all options
     * because state updates inside a async call updates only at the next render
     * for which the use effect causes a re-render as soon as the updated states cause a side-effect*/
  }, [
    statesOptions,
    disciplineOptions,
    specialityOptions,
    cityOptions,
    zipsOptions,
    allSpecialities,
    shiftTypesOptions,
  ]);

  const fetchCities = async (selectedStates: any = [], selectedCities: any = []) => {
    let result: any;
    const city = [];
    const zip = [];
    if (selectedCities?.length > 0) {
      result = await getLocationStatesCitiesZip({
        states: selectedStates,
        cities: selectedCities,
      });
      result.map(item => {
        zip.push(...item?.zips);
      });
      const zips = getMultiSelectOptions(zip || []);
      const sortedzips = getSortedData(zips, FilterType.string);
      const uniquezips = getUnique(
        sortedzips?.filter(x => x?.name),
        'name',
      );
      setZipsOptions(uniquezips);
    } else {
      result = await getLocationStatesCitiesZip({
        states: selectedStates,
        cities: [],
      });
      result.map(item => {
        city.push(...item?.cities);
      });
      result.map(item => {
        zip.push(...item?.zips);
      });
      const cities = getMultiSelectOptions(city || []);
      const sortedCities = getSortedData(cities, FilterType.string);
      const uniqueCities = getUnique(
        sortedCities?.filter(x => x?.name),
        'name',
      );
      setCityOptions(uniqueCities);
      const zips = getMultiSelectOptions(zip || []);
      const sortedzips = getSortedData(zips, FilterType.string);
      const uniquezips = getUnique(
        sortedzips?.filter(x => x?.name),
        'name',
      );
      setZipsOptions(uniquezips);
    }
  };

  const findSpecialities = disciplines => {
    const specialtiesArray: any[] = [];
    let specialties: any[] = [];
    if (disciplines) {
      specialties = allSpecialities.filter(
        curr => curr?.corelation?.slice(0, curr?.corelation?.indexOf('_')) === disciplines?.value,
      );
      if (specialties) {
        specialtiesArray.push(...specialties);
      }
      const sortedData = getSortedData(specialtiesArray, FilterType.string);
      const uniqueSpecialities = getUnique(
        sortedData?.filter(x => x?.name),
        'name',
      );
      setSpecialityOptions(uniqueSpecialities);
    } else {
      const uniqueSpecialityOptions = getUnique(
        allSpecialities.filter(x => x.name),
        'name',
      );
      setSpecialityOptions(uniqueSpecialityOptions);
    }
  };

  const getCandidateSkillsetsLabel = data => {
    if (data?.disciplineAbbr && data?.specialtyAbbr) {
      return `${data?.disciplineAbbr}-${data?.specialtyAbbr}`;
    } else if (data?.disciplineAbbr) {
      return data?.disciplineAbbr;
    } else {
      return data?.specialtyAbbr;
    }
  };

  React.useEffect(() => {
    const count = jobPreferencesData?.length >= 0 && jobPreferencesData?.length + 1;
    const skillSets = [];
    const uniqueVerifiedSkills = candidate?.verifiedSkills?.reduce((acc, curr) => {
      const duplicate = acc.find(
        item => item.disciplineId === curr.disciplineId && item.specialtyId === curr.specialtyId,
      );
      if (!duplicate) {
        acc.push(curr);
      }
      return acc;
    }, []);
    const uniqueUnVerifiedSkills = candidate?.unVerifiedSkills?.reduce((acc, curr) => {
      const duplicate = acc.find(
        item => item.disciplineId === curr.disciplineId && item.specialtyId === curr.specialtyId,
      );
      if (!duplicate) {
        acc.push(curr);
      }
      return acc;
    }, []);
    const verifiedSkills = uniqueVerifiedSkills?.map(item => {
      const value = {
        disciplineId: item?.disciplineId?.toString(),
        specialtyId: item?.specialtyId?.toString(),
      };
      skillSets.push(value);
      return {
        label: (
          <>
            <DoneAllIcon color="primary" /> {getCandidateSkillsetsLabel(item)}
          </>
        ),
        enableDelete: true,
        value: value,
      };
    });
    const unverifiedSkills = uniqueUnVerifiedSkills?.map(item => {
      const value = {
        disciplineId: item?.disciplineId?.toString(),
        specialtyId: item?.specialtyId?.toString(),
      };
      skillSets.push(value);
      return {
        label: (
          <>
            <HearingIcon /> {getCandidateSkillsetsLabel(item)}
          </>
        ),
        enableDelete: true,
        value: value,
      };
    });
    const candidateSkillsChips = [...verifiedSkills, ...unverifiedSkills];
    const defaultValues = {
      preferenceSetName: `Pref. Set ${count || ''}`,
      discipline: undefined,
      speciality: undefined,
      state: undefined,
      city: undefined,
      zip: undefined,
      radius: undefined,
      day: undefined,
      evening: undefined,
      night: undefined,
      notes: '',
      orderStartDateRange: undefined,
      skillSetsArray: [],
      locations: [],
      skillSets: [...skillSets],
      education: [],
      shifts: [],
    };
    if (id !== undefined && jobPreferencesData?.length > 0) {
      const data = jobPreferencesData?.filter(item => item?.id === id)?.[0];
      const preferenceData = {
        ...defaultValues,
        ...data,
      };
      Object.keys(preferenceData)?.forEach(key => {
        setValue(key, preferenceData[key]);
      });
      setPreferenceDetail(data);
      const dateRange = formatForDateRangePicker(data?.orderStartDateRange);
      setDateRangeDisplay(dateRange);
      getPreferencesChips('locations', data.locations);
      getPreferencesChips('skillSetsArray', data.skillSetsArray);
    } else {
      Object.keys(defaultValues)?.forEach(key => {
        setValue(key, defaultValues[key]);
      });
      setSkillSetChips({ skillSets: candidateSkillsChips });
      setValue(JobMatchFormFields.skillSets, skillSets);
    }
  }, [jobPreferencesData, id]);

  React.useEffect(() => {
    enableSaveOnChanges();
  }, [
    prefName,
    notes,
    locationValues,
    skillSetValues,
    day,
    evening,
    night,
    isSaving,
    orderStartDateRange,
    shifts,
    education,
  ]);

  return (
    <div>
      <Box>
        <div id="preferenceDetail">
          <Box>
            <Box>
              <FieldLabel label={'Preference Detail*'} />
              <Controller
                name={JobMatchFormFields.preferenceSetName}
                control={control}
                rules={{ required: 'Preference Set Name is required' }}
                render={({ field: { onChange, ...rest } }) => (
                  <TextField
                    disabled={isSaving}
                    className={classes.preferencename}
                    label="Preference Set Name*"
                    variant="filled"
                    error={!!error?.name}
                    helperText={error?.name ? preferenceNameErrorText : ''}
                    onChange={e => {
                      handleChange('preferenceSetName', e.target.value);
                    }}
                    {...rest}
                  />
                )}
              />
            </Box>
            <Box id="skillSets">
              <FieldLabel
                label={'Skill Sets*'}
                error={!!error.skillSets}
                errorText={t('candidate.jobPreferences.skillsetRequiredMessage')}
              />
              <Box className={classes.flex}>
                <Box className={classes.skillset}>
                  <Controller
                    name={JobMatchFormFields.discipline}
                    control={control}
                    render={({ field: { onChange, ...rest } }) => (
                      <JobPreferencesAutoComplete
                        disabled={isSaving}
                        options={disciplineOptions || []}
                        label="Discipline"
                        onChange={(e, val) => handleChange(JobMatchFormFields.discipline, val)}
                        {...rest}
                      />
                    )}
                  />
                </Box>
                <Box className={classes.skillset}>
                  <Controller
                    name={JobMatchFormFields.speciality}
                    control={control}
                    render={({ field: { onChange, ...rest } }) => (
                      <JobPreferencesAutoComplete
                        disabled={isSaving || !discipline}
                        options={specialityOptions || []}
                        label={
                          (discipline?.name == 'RN' || discipline?.name == 'LPN') && !speciality
                            ? 'Specialty*'
                            : 'Specialty'
                        }
                        onChange={(e, val) => handleChange(JobMatchFormFields.speciality, val)}
                        {...rest}
                      />
                    )}
                  />
                </Box>
                <CustomTooltip
                  tooltipContent={
                    (discipline?.name == 'RN' || discipline?.name == 'LPN') && !speciality
                      ? 'Specialty is required for RN and LPN'
                      : ''
                  }
                >
                  <IconButton
                    color="primary"
                    disabled={((discipline?.name == 'RN' || discipline?.name == 'LPN') && !speciality) || !discipline}
                    size="medium"
                    onClick={() => handleChipCreation('skillSetsArray')}
                    sx={{ marginTop: 3.5 }}
                  >
                    <Add />
                  </IconButton>
                </CustomTooltip>
              </Box>
              <div style={{ margin: '10px 0px', width: '600px', display: 'flex' }}>
                {skillSetChips.skillSets?.length !== 0 && (
                  <ChipsContainer
                    chips={skillSetChips}
                    onDelete={(group, chips, index) => handleChipDeletion(group, chips, index)}
                    onClick={() => null}
                  />
                )}
              </div>
            </Box>
            <Box id="location">
              <FieldLabel
                label={`${t('candidate.jobPreferences.location')}*`}
                error={!!error.locations}
                errorText={t('candidate.jobPreferences.locationInfoRequiredMessage')}
              />

              <Box className={classes.flex}>
                <Box className={classes.locations}>
                  <Controller
                    name={JobMatchFormFields.state}
                    control={control}
                    render={({ field: { onChange, ...rest } }) => (
                      <JobPreferencesAutoComplete
                        disabled={isSaving}
                        options={statesOptions || []}
                        label="State"
                        onChange={(e, val) => handleChange(JobMatchFormFields.state, val)}
                        {...rest}
                      />
                    )}
                  />
                </Box>
                <Box className={classes.locations}>
                  <Controller
                    name={JobMatchFormFields.city}
                    control={control}
                    render={({ field: { onChange, ...rest } }) => (
                      <JobPreferencesAutoComplete
                        disabled={isSaving || !state}
                        options={cityOptions || []}
                        label="City"
                        onInputChange={debounce(text => onTextSearch(text, 'c'), 1500)}
                        onChange={(e, val) => handleChange(JobMatchFormFields.city, val)}
                        {...rest}
                      />
                    )}
                  />
                </Box>
                <Box className={classes.locations}>
                  <Controller
                    name={JobMatchFormFields.zip}
                    control={control}
                    render={({ field: { onChange, ...rest } }) => (
                      <JobPreferencesAutoComplete
                        disabled={isSaving}
                        options={zipsOptions || []}
                        label="Zip"
                        onChange={(e, val) => handleChange(JobMatchFormFields.zip, val)}
                        onInputChange={debounce(text => onTextSearch(text, 'z'), 1500)}
                        {...rest}
                      />
                    )}
                  />
                </Box>
                <Box className={classes.locations}>
                  <Controller
                    name={JobMatchFormFields.radius}
                    control={control}
                    defaultValue={''}
                    render={({ field: { onChange, ...rest } }) => (
                      <>
                        {hasTruthyRadius ? (
                          <Tooltip title="Only 1 location radius allowed per preference">
                            <TextField disabled label="Radius" variant="filled" {...rest} />
                          </Tooltip>
                        ) : (
                          <TextField
                            disabled={isSaving || !zip}
                            label="Radius"
                            variant="filled"
                            onChange={e => handleChange(JobMatchFormFields.radius, e.target.value)}
                            error={!!error.radius}
                            helperText={error.radius ? radiusErrorText : ''}
                            onBlur={e => console.log(e, e.target.value)}
                            {...rest}
                          />
                        )}
                      </>
                    )}
                  />
                </Box>
                <Box>
                  <IconButton
                    color="primary"
                    disabled={!(state || city || zip || radius)}
                    size="medium"
                    onClick={() => handleChipCreation('locations')}
                    sx={{ marginTop: 3, marginLeft: 0.5 }}
                  >
                    <Add />
                  </IconButton>
                </Box>
              </Box>
              <div style={{ margin: '10px 0px', width: '600px', display: 'flex' }}>
                {locationChips.locations?.length !== 0 && (
                  <ChipsContainer
                    chips={locationChips}
                    onDelete={(group, chips, index) => handleChipDeletion(group, chips, index)}
                    onClick={() => null}
                  />
                )}
              </div>
            </Box>
            <Box id="shifts" className={classes.education}>
              <FieldLabel label={'Shifts'} />
              <Box>
                <Controller
                  name={JobMatchFormFields.shifts}
                  control={control}
                  render={({ field: { onChange, ...rest } }) => (
                    <JobPreferencesAutoComplete
                      disabled={isSaving}
                      options={shiftTypesOptions || []}
                      label="Shifts"
                      onChange={(e, val) => handleChange(JobMatchFormFields.shifts, val)}
                      multiSelect
                      {...rest}
                    />
                  )}
                />
              </Box>
            </Box>
            <Box id="orderStartDateRange">
              <FieldLabel label={'Order Start Date Range'} />
              <Controller
                name={JobMatchFormFields.orderStartDateRange}
                control={control}
                render={({ field }) => (
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DateRangePicker
                      placeholder="Order Start Date Range"
                      variant="filled"
                      trailingIconShow={true}
                      maxWidth={318}
                      onCancel={() => clearDates()}
                      onApply={e => {
                        e?.startDate && e?.endDate && setDateRangeDisplay(formatForDateRangePicker(e));
                      }}
                      initialDateRange={
                        dateRangeDisplay
                          ? {
                              startDate: new Date(dateRangeDisplay?.startDate),
                              endDate: new Date(dateRangeDisplay?.endDate),
                              useLabelAsValue: true,
                            }
                          : null
                      }
                      clickAway={true}
                      disablePortal={true}
                      quickSelect={false}
                      value={dateRangeDisplay}
                      fullWidth={true}
                      labelClass={classes.dateRangeLabel}
                      onChange={() => null}
                    />
                  </LocalizationProvider>
                )}
              />
              <div style={{ margin: '10px 0px', width: '600px', display: 'flex' }}>
                {orderDateRangeChip.startDate?.length > 0 && (
                  <ChipsContainer
                    chips={orderDateRangeChip}
                    onDelete={(group, chips, index) => clearDates()}
                    onClick={() => null}
                  />
                )}
              </div>
            </Box>
            <Box id="education" className={classes.education}>
              <FieldLabel label={'Education'} />
              <Box>
                <Controller
                  name={JobMatchFormFields.education}
                  control={control}
                  render={({ field: { onChange, ...rest } }) => (
                    <JobPreferencesAutoComplete
                      disabled={isSaving}
                      options={educationOptions || []}
                      label="Education"
                      multiSelect={true}
                      onChange={(e, val) => handleChange(JobMatchFormFields.education, val)}
                      defaultValue={preferenceDetail?.education}
                      {...rest}
                    />
                  )}
                />
              </Box>
            </Box>
            <Box id="notes">
              <FieldLabel label={'Notes'} />
              <Controller
                name={JobMatchFormFields.notes}
                control={control}
                rules={{ required: 'Preference Set Name is required' }}
                defaultValue={''}
                render={({ field: { onChange, ...rest } }) => (
                  <TextField
                    disabled={isSaving}
                    className={classes.notes}
                    id="PreferenceSetName"
                    label="Notes"
                    variant="filled"
                    placeholder="Type Your Notes Here"
                    multiline
                    onChange={e => handleChange(JobMatchFormFields.notes, e.target.value)}
                    {...rest}
                  />
                )}
              />
            </Box>
            <Controller
              name={'locations'}
              control={control}
              render={({ field: { onChange, ...rest } }) => <input {...rest} style={{ display: 'none' }} hidden />}
            />
            <Controller
              name={'skillSetsArray'}
              control={control}
              render={({ field: { onChange, ...rest } }) => <input {...rest} style={{ display: 'none' }} hidden />}
            />
            <Controller
              name={'skillSets'}
              control={control}
              render={({ field: { onChange, ...rest } }) => <input {...rest} style={{ display: 'none' }} hidden />}
            />
          </Box>
        </div>
      </Box>
    </div>
  );
};

export default OrderPreferencesForm;
